import { useState, useEffect, useCallback } from 'react';
import { RefreshCw, AlertTriangle, Receipt, Plus, Trash2, Check, Star } from 'lucide-react';
import {
  getRatePlans, createRatePlan, updateRatePlan, deleteRatePlan,
  assignRatePlan, getLineRental,
} from '../services/portalApi';

const CURRENCY_SYMBOL = { GBP: '£', EUR: '€', USD: '$' };

// minor units (pence) -> "£12.34"
function fmtMoney(minor, currency) {
  const sym = CURRENCY_SYMBOL[currency] || `${currency} `;
  return `${sym}${(Number(minor || 0) / 100).toFixed(2)}`;
}
// "12.34" (major) -> 1234 (minor)
function toMinor(major) {
  return Math.max(0, Math.round(Number(major || 0) * 100));
}

function thisMonth() {
  return new Date().toISOString().slice(0, 7);
}

export default function Billing() {
  const [plans,   setPlans]   = useState([]);
  const [rental,  setRental]  = useState(null);
  const [period,  setPeriod]  = useState(thisMonth());
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  // New-plan form
  const [adding,  setAdding]  = useState(false);
  const [form,    setForm]    = useState({ name: '', currency: 'GBP', rate: '', vat_pct: '20' });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [p, r] = await Promise.all([getRatePlans(), getLineRental(period)]);
      setPlans(p.ratePlans || []);
      setRental(r);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!form.name.trim()) { alert('Plan name is required'); return; }
    try {
      await createRatePlan({
        name: form.name.trim(),
        currency: form.currency,
        line_rental_minor: toMinor(form.rate),
        vat_pct: Number(form.vat_pct) || 0,
      });
      setForm({ name: '', currency: 'GBP', rate: '', vat_pct: '20' });
      setAdding(false);
      await load();
    } catch (e) { alert('Create failed: ' + e.message); }
  };

  const handleSavePlan = async (plan, patch) => {
    try {
      await updateRatePlan(plan.id, patch);
      await load();
    } catch (e) { alert('Update failed: ' + e.message); }
  };

  const handleDelete = async (plan) => {
    if (plan.is_default) { alert('Make another plan the default before deleting this one.'); return; }
    if (!window.confirm(`Delete rate plan "${plan.name}"? Customers on it fall back to the default plan.`)) return;
    try { await deleteRatePlan(plan.id); await load(); }
    catch (e) { alert('Delete failed: ' + e.message); }
  };

  const handleAssign = async (customerId, ratePlanId) => {
    try {
      await assignRatePlan(customerId, ratePlanId === '' ? null : Number(ratePlanId));
      await load();
    } catch (e) { alert('Assign failed: ' + e.message); }
  };

  const currency = rental?.currency || 'GBP';

  return (
    <div className="content">
      {error && (
        <div style={{ padding: '12px 16px', marginBottom: 16, display: 'flex', gap: 8, alignItems: 'center', color: 'var(--err)', fontSize: 13, background: 'var(--err-bg)', border: '1px solid var(--err-border)', borderRadius: 8 }}>
          <AlertTriangle size={14}/> {error}
        </div>
      )}

      {/* ── Rate plans ── */}
      <div className="card mb-20">
        <div className="card-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Receipt size={15} color="var(--info)"/>
            <div className="card-title">Rate Plans</div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-sm" onClick={() => setAdding(a => !a)}>
              <Plus size={12}/> New plan
            </button>
            <button className="btn btn-sm" onClick={load} disabled={loading}><RefreshCw size={11}/></button>
          </div>
        </div>

        {adding && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', padding: '12px 20px', borderBottom: '1px solid var(--border)' }}>
            <Field label="Name">
              <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} style={inputStyle} placeholder="e.g. Standard"/>
            </Field>
            <Field label="Currency">
              <select value={form.currency} onChange={e => setForm(f => ({ ...f, currency: e.target.value }))} style={inputStyle}>
                <option>GBP</option><option>EUR</option><option>USD</option>
              </select>
            </Field>
            <Field label="Line rental / number / month">
              <input type="number" min="0" step="0.01" value={form.rate} onChange={e => setForm(f => ({ ...f, rate: e.target.value }))} style={inputStyle} placeholder="0.00"/>
            </Field>
            <Field label="VAT %">
              <input type="number" min="0" step="0.1" value={form.vat_pct} onChange={e => setForm(f => ({ ...f, vat_pct: e.target.value }))} style={{ ...inputStyle, width: 70 }}/>
            </Field>
            <button className="btn btn-sm" onClick={handleCreate}><Check size={12}/> Save</button>
          </div>
        )}

        <div className="table-wrap">
          <table>
            <thead>
              <tr><th>Plan</th><th>Currency</th><th>Line rental / no. / mo</th><th>VAT %</th><th>Customers</th><th></th></tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--muted-3)', padding: '24px 0' }}>Loading…</td></tr>
              ) : plans.length === 0 ? (
                <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--muted-3)', padding: '24px 0' }}>No rate plans.</td></tr>
              ) : plans.map(p => <PlanRow key={p.id} plan={p} onSave={handleSavePlan} onDelete={handleDelete}/>)}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Line rental ── */}
      <div className="card">
        <div className="card-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Receipt size={15} color="var(--info)"/>
            <div className="card-title">Line Rental</div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input type="month" value={period} onChange={e => setPeriod(e.target.value)} style={inputStyle}/>
          </div>
        </div>

        <div style={{ padding: '6px 20px', fontSize: 12, color: 'var(--muted-3)' }}>
          Recurring charge = in-service numbers mapped to each customer × their plan's monthly rate. A snapshot of the current inventory.
        </div>

        <div className="table-wrap">
          <table>
            <thead>
              <tr><th>Customer</th><th>Plan</th><th>Numbers</th><th>Rate</th><th>Net</th><th>VAT</th><th>Total</th></tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} style={{ textAlign: 'center', color: 'var(--muted-3)', padding: '24px 0' }}>Loading…</td></tr>
              ) : !rental?.lines?.length ? (
                <tr><td colSpan={7} style={{ textAlign: 'center', color: 'var(--muted-3)', padding: '24px 0' }}>No customers.</td></tr>
              ) : rental.lines.map(l => (
                <tr key={l.customer_id}>
                  <td style={{ color: 'var(--text)' }}>{l.customer_name}</td>
                  <td>
                    <select
                      value={l.rate_plan_id ?? ''}
                      onChange={e => handleAssign(l.customer_id, e.target.value)}
                      style={{ ...inputStyle, minWidth: 130 }}
                    >
                      <option value="">Default{plans.find(p => p.is_default) ? ` (${plans.find(p => p.is_default).name})` : ''}</option>
                      {plans.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </td>
                  <td style={{ color: 'var(--text-2)' }}>{l.number_count}</td>
                  <td style={{ color: 'var(--muted)' }}>{fmtMoney(l.rate_minor, l.currency)}</td>
                  <td style={{ color: 'var(--text-2)' }}>{fmtMoney(l.net_minor, l.currency)}</td>
                  <td style={{ color: 'var(--muted)' }}>{fmtMoney(l.vat_minor, l.currency)} <span style={{ color: 'var(--muted-3)', fontSize: 11 }}>({l.vat_pct}%)</span></td>
                  <td style={{ color: 'var(--ok)', fontWeight: 600 }}>{fmtMoney(l.gross_minor, l.currency)}</td>
                </tr>
              ))}
            </tbody>
            {rental?.totals && (
              <tfoot>
                <tr style={{ borderTop: '2px solid var(--border-strong)', fontWeight: 600 }}>
                  <td style={{ color: 'var(--text)' }}>Total</td>
                  <td></td>
                  <td style={{ color: 'var(--text-2)' }}>{rental.totals.number_count}</td>
                  <td></td>
                  <td style={{ color: 'var(--text-2)' }}>{fmtMoney(rental.totals.net_minor, currency)}</td>
                  <td style={{ color: 'var(--muted)' }}>{fmtMoney(rental.totals.vat_minor, currency)}</td>
                  <td style={{ color: 'var(--ok)' }}>{fmtMoney(rental.totals.gross_minor, currency)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
}

const inputStyle = {
  background: 'var(--bg)', border: '1px solid var(--border-strong)', borderRadius: 6,
  color: 'var(--text-2)', padding: '5px 10px', fontSize: 12, outline: 'none',
};

function Field({ label, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: 11, color: 'var(--muted-2)' }}>{label}</span>
      {children}
    </div>
  );
}

function PlanRow({ plan, onSave, onDelete }) {
  const [edit, setEdit] = useState(false);
  const [rate, setRate] = useState((plan.line_rental_minor / 100).toFixed(2));
  const [vat,  setVat]  = useState(String(plan.vat_pct));

  const commit = () => {
    onSave(plan, { line_rental_minor: toMinor(rate), vat_pct: Number(vat) || 0 });
    setEdit(false);
  };

  return (
    <tr>
      <td style={{ color: 'var(--text)' }}>
        {plan.name}
        {plan.is_default ? <span style={{ marginLeft: 6, fontSize: 10, color: 'var(--info)' }}><Star size={10} style={{ verticalAlign: 'middle' }}/> default</span> : null}
      </td>
      <td style={{ color: 'var(--muted)' }}>{plan.currency}</td>
      <td>
        {edit
          ? <input type="number" min="0" step="0.01" value={rate} onChange={e => setRate(e.target.value)} style={{ ...inputStyle, width: 90 }}/>
          : <span style={{ color: 'var(--text-2)' }}>{fmtMoney(plan.line_rental_minor, plan.currency)}</span>}
      </td>
      <td>
        {edit
          ? <input type="number" min="0" step="0.1" value={vat} onChange={e => setVat(e.target.value)} style={{ ...inputStyle, width: 64 }}/>
          : <span style={{ color: 'var(--muted)' }}>{plan.vat_pct}%</span>}
      </td>
      <td style={{ color: 'var(--muted)' }}>{plan.customer_count}</td>
      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
        {edit ? (
          <>
            <button className="btn btn-sm" onClick={commit}><Check size={11}/></button>
            <button className="btn btn-sm" style={{ marginLeft: 6 }} onClick={() => setEdit(false)}>Cancel</button>
          </>
        ) : (
          <>
            <button className="btn btn-sm" onClick={() => setEdit(true)}>Edit</button>
            {!plan.is_default && (
              <>
                <button className="btn btn-sm" style={{ marginLeft: 6 }} title="Make default" onClick={() => onSave(plan, { is_default: true })}><Star size={11}/></button>
                <button className="btn btn-sm" style={{ marginLeft: 6 }} title="Delete" onClick={() => onDelete(plan)}><Trash2 size={11}/></button>
              </>
            )}
          </>
        )}
      </td>
    </tr>
  );
}
